<template>
  <div class="mz-desktop">

    <!-- ══ SIDEBAR NAV ══ -->
    <aside class="mz-sidebar">
      <div class="sidebar-top">
        <div class="sidebar-brand">
          <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="14" fill="#d48c1c"/>
            <path d="M12 34V18l8 10 4-6 4 6 8-10v16" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          <span class="sidebar-wordmark">Muzilly</span>
        </div>
        <nav class="sidebar-nav">
          <button v-for="tab in navTabs" :key="tab.id"
            class="nav-item" :class="{ active: tab.id === 'messages' }"
            @click="navigate(tab.id)">
            <span class="nav-item-icon" v-html="tab.svg"></span>
            <span class="nav-item-label">{{ tab.label }}</span>
            <span v-if="tab.badge" class="nav-badge">{{ tab.badge }}</span>
          </button>
        </nav>
      </div>
      <div class="sidebar-user">
        <div class="su-av" :style="{ background: roleColors[currentRole] }">{{ userInitials }}</div>
        <div class="su-info">
          <div class="su-name">{{ staticUser[currentRole].name }}</div>
          <div class="su-role">{{ roleLabelMap[currentRole] }}</div>
        </div>
        <button class="su-logout" @click="navigate('home')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
    </aside>

    <!-- ══ MAIN CONTENT ══ -->
    <div class="mz-content">
      <header class="mz-topbar">
        <div class="topbar-left">
          <h2 class="topbar-title">Messages</h2>
        </div>
        <div class="topbar-right">
          <div class="role-switcher">
            <button v-for="r in ['fan','artist','rising']" :key="r"
              class="role-pill" :class="{ active: currentRole === r }"
              @click="currentRole = r; activeThread = null">{{ rolePillLabel[r] }}</button>
          </div>
          <button class="topbar-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span class="t-badge">5</span>
          </button>
        </div>
      </header>

      <!-- ══════════ MESSAGES LAYOUT ══════════ -->
      <div class="messages-layout animate-up">

        <!-- ── LEFT: Thread list ── -->
        <div class="thread-list">

          <!-- Artist: broadcast button + tab filter -->
          <div v-if="currentRole === 'artist'" class="tl-artist-head">
            <button class="broadcast-btn" @click="showBroadcast = !showBroadcast">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              Broadcast to subscribers
            </button>
          </div>

          <!-- Fan: inbox tabs -->
          <div class="tl-tabs">
            <button class="tlt" :class="{ active: threadTab === 'all' }" @click="threadTab = 'all'">All</button>
            <button v-if="currentRole === 'artist'" class="tlt" :class="{ active: threadTab === 'fans' }" @click="threadTab = 'fans'">
              Fans <span class="tlt-count">{{ fanThreads.length }}</span>
            </button>
            <button v-if="currentRole === 'artist'" class="tlt" :class="{ active: threadTab === 'artists' }" @click="threadTab = 'artists'">Artists</button>
            <button v-if="currentRole !== 'artist'" class="tlt" :class="{ active: threadTab === 'artists' }" @click="threadTab = 'artists'">Artists</button>
            <button v-if="currentRole === 'rising'" class="tlt" :class="{ active: threadTab === 'peers' }" @click="threadTab = 'peers'">Peers</button>
          </div>

          <!-- Broadcast form -->
          <div v-if="showBroadcast && currentRole === 'artist'" class="broadcast-form animate-up">
            <div class="bf-label">Message all {{ broadcastCount }} subscribers</div>
            <textarea v-model="broadcastMsg" class="bf-input" placeholder="Share something with your subscribers…"></textarea>
            <div class="bf-foot">
              <button class="bf-cancel" @click="showBroadcast = false; broadcastMsg = ''">Cancel</button>
              <button class="bf-send" @click="sendBroadcast">Send to all →</button>
            </div>
          </div>

          <!-- Thread items -->
          <div class="thread-items">
            <div v-for="thread in visibleThreads" :key="thread.id"
              class="thread-item" :class="{ active: activeThread && activeThread.id === thread.id, unread: thread.unread }"
              @click="openThread(thread)">
              <div class="ti-av-wrap">
                <div class="ti-av" :style="{ background: thread.color }">{{ thread.initials }}</div>
                <div v-if="thread.online" class="ti-online"></div>
              </div>
              <div class="ti-body">
                <div class="ti-top">
                  <span class="ti-name">{{ thread.name }}</span>
                  <span class="ti-time">{{ thread.time }}</span>
                </div>
                <div class="ti-context">{{ thread.context }}</div>
                <div class="ti-preview" :class="{ 'ti-preview-unread': thread.unread }">{{ thread.preview }}</div>
              </div>
              <div v-if="thread.unread" class="ti-unread-dot"></div>
            </div>

            <!-- Fan: locked artists (haven't paid yet) -->
            <div v-if="currentRole === 'fan'" class="locked-artists-section">
              <div class="las-label">Artists you follow · pay to message</div>
              <div v-for="a in lockedArtists" :key="a.id" class="locked-artist-row" @click="showPaywall(a)">
                <div class="lar-av" :style="{ background: a.color }">{{ a.initials }}</div>
                <div class="lar-info">
                  <div class="lar-name">{{ a.name }}</div>
                  <div class="lar-genre">{{ a.genre }}</div>
                </div>
                <div class="lar-price">
                  <span v-if="a.subscribed" class="lar-discount">$1.00</span>
                  <span v-else class="lar-full">$3.00</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── RIGHT: Active thread or empty state ── -->
        <div class="thread-view">

          <!-- Paywall modal -->
          <div v-if="paywallArtist" class="paywall-overlay" @click.self="paywallArtist = null">
            <div class="paywall-card animate-up">
              <div class="pw-av" :style="{ background: paywallArtist.color }">{{ paywallArtist.initials }}</div>
              <div class="pw-name">{{ paywallArtist.name }}</div>
              <div class="pw-genre">{{ paywallArtist.genre }}</div>
              <div class="pw-divider"></div>
              <div class="pw-title">Unlock direct messaging</div>
              <div class="pw-desc">Pay a one-time fee to open a permanent thread with {{ paywallArtist.name }}. Once unlocked, you can message freely — forever.</div>
              <div class="pw-breakdown">
                <div class="pwb-row">
                  <span>Thread unlock</span>
                  <span v-if="paywallArtist.subscribed" class="pwb-discount">
                    <span class="pwb-strike">$3.00</span>
                    <span class="pwb-new">$1.00</span>
                  </span>
                  <span v-else>$3.00</span>
                </div>
                <div v-if="paywallArtist.subscribed" class="pwb-row pwb-sub-note">
                  <span>Subscriber discount applied</span>
                  <span class="teal">–$2.00</span>
                </div>
                <div class="pwb-row pwb-total">
                  <span>Total</span>
                  <span class="amber">{{ paywallArtist.subscribed ? '$1.00' : '$3.00' }}</span>
                </div>
              </div>
              <div class="pw-split-note">{{ paywallArtist.name }} earns 70% · Muzilly 30%</div>
              <button class="pw-pay-btn" @click="unlockThread(paywallArtist)">
                Pay {{ paywallArtist.subscribed ? '$1.00' : '$3.00' }} · Unlock thread →
              </button>
              <button class="pw-cancel" @click="paywallArtist = null">Maybe later</button>
            </div>
          </div>

          <!-- Broadcast success -->
          <div v-if="broadcastSent" class="broadcast-success animate-up">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#5dcaa5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Broadcast sent to {{ broadcastCount }} subscribers
          </div>

          <!-- Empty state -->
          <div v-if="!activeThread" class="thread-empty">
            <div class="te-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="rgba(245,240,232,0.2)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <div class="te-title">Select a conversation</div>
            <div v-if="currentRole === 'fan'" class="te-sub">Pay to unlock a thread with any artist you follow</div>
            <div v-else class="te-sub">Your messages live here</div>
          </div>

          <!-- Active thread -->
          <div v-if="activeThread" class="thread-open">
            <div class="to-head">
              <div class="toh-av" :style="{ background: activeThread.color }">{{ activeThread.initials }}</div>
              <div class="toh-info">
                <div class="toh-name">{{ activeThread.name }}</div>
                <div class="toh-context">{{ activeThread.context }}</div>
              </div>
              <div v-if="activeThread.unlocked" class="toh-unlocked">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="#5dcaa5" stroke-width="1.5"/><path d="M7 11V7a4 4 0 018 0v4" stroke="#5dcaa5" stroke-width="1.5" stroke-linecap="round"/></svg>
                Thread unlocked
              </div>
              <!-- Artist: fan paid context -->
              <div v-if="currentRole === 'artist' && activeThread.paidBy" class="toh-paid-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#d48c1c" stroke-width="1.5"/><path d="M12 6v6l4 2" stroke="#d48c1c" stroke-width="1.5" stroke-linecap="round"/></svg>
                Paid ${{ activeThread.paidAmount }} to message you · you earned ${{ activeThread.artistEarned }}
              </div>
            </div>

            <div class="to-messages" ref="messageScroll">
              <div v-for="msg in activeThread.messages" :key="msg.id" class="msg-bubble-wrap" :class="{ 'is-mine': msg.mine }">
                <div v-if="!msg.mine" class="mb-av" :style="{ background: activeThread.color }">{{ activeThread.initials }}</div>
                <div class="msg-bubble" :class="{ 'bubble-mine': msg.mine, 'bubble-theirs': !msg.mine }">
                  <div class="mb-text">{{ msg.text }}</div>
                  <div class="mb-time">{{ msg.time }}</div>
                </div>
              </div>
            </div>

            <!-- Input area -->
            <div class="to-input">
              <!-- Fan: locked thread -->
              <div v-if="currentRole === 'fan' && !activeThread.unlocked" class="input-locked">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                <span>Unlock this thread to reply</span>
                <button class="il-cta" @click="showPaywall(activeThread)">Unlock →</button>
              </div>
              <!-- Open input -->
              <div v-else class="input-open">
                <input v-model="messageInput" type="text" class="msg-input"
                  :placeholder="'Message ' + activeThread.name + '…'"
                  @keyup.enter="sendMessage"/>
                <button class="msg-send" @click="sendMessage">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MessagesView",
  data() {
    return {
      currentRole: "fan",
      activeThread: null,
      threadTab: "all",
      showBroadcast: false,
      broadcastMsg: "",
      broadcastSent: false,
      broadcastCount: 847,
      messageInput: "",
      paywallArtist: null,
      rolePillLabel: { fan:"Fan view", artist:"Artist view", rising:"Rising view" },
      roleLabelMap:  { fan:"Fan member", artist:"Verified artist", rising:"Rising artist" },
      roleColors:    { fan:"#2e5b7e", artist:"#8b3a3a", rising:"#6b3a5a" },
      staticUser:    { fan:{name:"Jordan"}, artist:{name:"Ayo Blaze"}, rising:{name:"Mara Sol"} },

      // Fan threads — only ones they've already unlocked
      fanThreads: [
        {
          id:1, name:"Ayo Blaze", initials:"AB", color:"#8b3a3a",
          context:"Subscribed since Jan 2025", time:"2h ago",
          preview:"Next track dropping end of the month 🔥", unread:true, online:true,
          unlocked:true, paidBy:false, paidAmount:null, artistEarned:null,
          messages:[
            { id:1, mine:false, text:"Hey, glad you're a subscriber. You caught the room last night?", time:"Yesterday 9:14 PM" },
            { id:2, mine:true,  text:"Yes!! That unreleased cut was insane, when does it drop?", time:"Yesterday 9:22 PM" },
            { id:3, mine:false, text:"Next track dropping end of the month. You heard it here first 🔥", time:"2h ago" },
          ],
        },
      ],

      // Artist threads — mix of fans who paid and fellow artists
      artistThreads: [
        {
          id:10, name:"Keiko Balogun", initials:"KB", color:"#2e5b7e",
          context:"Fan · paid $3 to message", time:"1h ago",
          preview:"I've been a subscriber for 3 months and your music has genuinely…", unread:true, online:false,
          unlocked:true, paidBy:true, paidAmount:"3.00", artistEarned:"2.10",
          messages:[
            { id:1, mine:false, text:"Hey Ayo! I've been a subscriber for 3 months and your music has genuinely changed how I feel about Afrobeats. Is there any chance you're playing live this year?", time:"1h ago" },
          ],
        },
        {
          id:11, name:"Luna Wave", initials:"LW", color:"#2e5b7e",
          context:"Verified artist", time:"3h ago",
          preview:"I heard your new demo, we should link up", unread:false, online:true,
          unlocked:true, paidBy:false, paidAmount:null, artistEarned:null,
          messages:[
            { id:1, mine:false, text:"Yo, I heard your new demo from the room last night. That beat on Sundown is crazy.", time:"4h ago" },
            { id:2, mine:true,  text:"Appreciate that, Mixx produced it. We should link up and work.", time:"3h ago" },
            { id:3, mine:false, text:"I heard your new demo, we should link up", time:"3h ago" },
          ],
        },
        {
          id:12, name:"Marcus Torres", initials:"MT", color:"#4a3a6b",
          context:"Fan · paid $3 to message", time:"Yesterday",
          preview:"Would you ever do a meet and greet for subscribers?", unread:false, online:false,
          unlocked:true, paidBy:true, paidAmount:"3.00", artistEarned:"2.10",
          messages:[
            { id:1, mine:false, text:"Would you ever do a meet and greet for subscribers? Even a virtual one?", time:"Yesterday" },
            { id:2, mine:true,  text:"That's actually a great idea. Let me think about it for the next room.", time:"Yesterday" },
          ],
        },
      ],

      // Rising threads
      risingThreads: [
        {
          id:20, name:"Luna Wave", initials:"LW", color:"#2e5b7e",
          context:"Co-signed your demo · Midnight Run", time:"2 days ago",
          preview:"Your tone is something special, keep going", unread:false, online:true,
          unlocked:true, paidBy:false, paidAmount:null, artistEarned:null,
          messages:[
            { id:1, mine:false, text:"I co-signed your demo because your tone is something I haven't heard in years. Keep going.", time:"2 days ago" },
            { id:2, mine:true,  text:"That means everything to me honestly. Thank you for listening.", time:"2 days ago" },
            { id:3, mine:false, text:"Your tone is something special, keep going", time:"2 days ago" },
          ],
        },
      ],

      // Locked artists (fan hasn't paid yet)
      lockedArtists: [
        { id:1, name:"Luna Wave",   initials:"LW", color:"#2e5b7e", genre:"R&B",      subscribed:false },
        { id:2, name:"Kxng Draco",  initials:"KD", color:"#4a3a6b", genre:"Hip-Hop",  subscribed:false },
        { id:3, name:"Sade Femi",   initials:"SF", color:"#3a6b4a", genre:"Amapiano", subscribed:true  },
        { id:4, name:"Cole Rivers", initials:"CR", color:"#7e5a2e", genre:"Soul",     subscribed:false },
      ],

      navTabs: [
        { id:"home",     label:"Home",     badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"search",   label:"Discover", badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.5"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>` },
        { id:"drop",     label:"Drop",     badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><path d="M12 8v8M8 12l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"rooms",    label:"Rooms",    badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>` },
        { id:"messages", label:"Messages", badge:"3",  svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"collab",   label:"Collab",   badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        { id:"profile",  label:"Profile",  badge:null, svg:`<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/></svg>` },
      ],
    };
  },
  computed: {
    userInitials() {
      return (this.staticUser[this.currentRole]?.name || "").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    },
    visibleThreads() {
      if (this.currentRole === 'fan') return this.fanThreads;
      if (this.currentRole === 'artist') {
        if (this.threadTab === 'fans') return this.artistThreads.filter(t => t.paidBy);
        if (this.threadTab === 'artists') return this.artistThreads.filter(t => !t.paidBy);
        return this.artistThreads;
      }
      if (this.currentRole === 'rising') return this.risingThreads;
      return [];
    },
  },
  mounted() {
    if (this.$route && this.$route.query.role) {
      this.currentRole = this.$route.query.role;
    }
  },
  methods: {
    navigate(tabId) {
      if (tabId === 'messages') return;
      const routed = ['profile', 'drop', 'collab'];
      if (routed.includes(tabId)) {
        this.$router.push({ path: '/' + tabId, query: { role: this.currentRole } });
        return;
      }
      this.$router.push(tabId === 'home' ? '/' : '/' + tabId);
    },
    openThread(thread) {
      this.activeThread = thread;
      thread.unread = false;
      this.paywallArtist = null;
    },
    showPaywall(artist) {
      this.paywallArtist = artist;
    },
    unlockThread(artist) {
      // Move from locked to unlocked threads
      const newThread = {
        id: Date.now(),
        name: artist.name,
        initials: artist.initials,
        color: artist.color,
        context: artist.subscribed ? 'Subscriber · unlocked for $1' : 'Unlocked for $3',
        time: 'Just now',
        preview: 'Thread unlocked — say something',
        unread: false,
        online: false,
        unlocked: true,
        paidBy: false,
        messages: [],
      };
      this.fanThreads.push(newThread);
      this.lockedArtists = this.lockedArtists.filter(a => a.id !== artist.id);
      this.paywallArtist = null;
      this.activeThread = newThread;
    },
    sendMessage() {
      if (!this.messageInput.trim() || !this.activeThread) return;
      this.activeThread.messages.push({
        id: Date.now(),
        mine: true,
        text: this.messageInput.trim(),
        time: 'Just now',
      });
      this.activeThread.preview = this.messageInput.trim();
      this.messageInput = "";
    },
    sendBroadcast() {
      if (!this.broadcastMsg.trim()) return;
      this.showBroadcast = false;
      this.broadcastSent = true;
      this.broadcastMsg = "";
      setTimeout(() => { this.broadcastSent = false; }, 4000);
    },
  },
};
</script>

<style scoped>
*{box-sizing:border-box;margin:0;padding:0}
.mz-desktop{display:grid;grid-template-columns:220px 1fr;min-height:100vh;background:#0c0a0e;font-family:'DM Sans','Helvetica Neue',sans-serif;color:#f5f0e8}
.mz-sidebar{background:#100e13;border-right:0.5px solid rgba(245,240,232,0.06);display:flex;flex-direction:column;position:sticky;top:0;height:100vh;overflow-y:auto}
.sidebar-top{flex:1;padding:24px 0 16px}
.sidebar-brand{display:flex;align-items:center;gap:10px;padding:0 20px 24px}
.sidebar-wordmark{font-size:19px;font-weight:500;color:#f5f0e8;letter-spacing:-0.3px}
.sidebar-nav{display:flex;flex-direction:column;gap:2px;padding:0 10px}
.nav-item{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:9px;background:none;border:none;cursor:pointer;color:rgba(245,240,232,0.4);font-size:14px;font-family:inherit;text-align:left;transition:all .18s}
.nav-item:hover{background:rgba(245,240,232,0.04);color:rgba(245,240,232,0.75)}
.nav-item.active{background:rgba(212,140,28,0.1);color:#d48c1c}
.nav-item-icon{display:flex;align-items:center;flex-shrink:0}
.nav-item-label{flex:1}
.nav-badge{background:#d48c1c;color:#0c0a0e;font-size:10px;font-weight:700;border-radius:10px;padding:1px 6px;min-width:18px;text-align:center}
.sidebar-user{display:flex;align-items:center;gap:10px;padding:14px 18px;border-top:0.5px solid rgba(245,240,232,0.06)}
.su-av{width:32px;height:32px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;color:rgba(255,255,255,0.9)}
.su-info{flex:1;min-width:0}
.su-name{font-size:12px;font-weight:500;color:#f5f0e8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.su-role{font-size:11px;color:rgba(245,240,232,0.4);margin-top:1px}
.su-logout{background:none;border:none;cursor:pointer;color:rgba(245,240,232,0.3);padding:3px;transition:color .2s;flex-shrink:0}
.su-logout:hover{color:rgba(245,240,232,0.65)}
.mz-content{display:flex;flex-direction:column;min-height:100vh;overflow:hidden}
.mz-topbar{display:flex;align-items:center;justify-content:space-between;padding:14px 28px;background:#0c0a0e;border-bottom:0.5px solid rgba(245,240,232,0.06);position:sticky;top:0;z-index:50}
.topbar-left{display:flex;align-items:center;gap:10px}
.topbar-title{font-size:17px;font-weight:500;color:#f5f0e8;letter-spacing:-0.2px}
.topbar-right{display:flex;align-items:center;gap:10px}
.role-switcher{display:flex;gap:5px}
.role-pill{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:20px;padding:4px 13px;font-size:12px;color:rgba(245,240,232,0.4);font-family:inherit;cursor:pointer;transition:all .18s}
.role-pill.active{background:rgba(212,140,28,0.12);border-color:rgba(212,140,28,0.4);color:#d48c1c}
.topbar-icon{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:8px;width:34px;height:34px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:rgba(245,240,232,0.5);position:relative;transition:all .18s}
.t-badge{position:absolute;top:-5px;right:-5px;width:14px;height:14px;border-radius:50%;background:#d48c1c;color:#0c0a0e;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center}
.amber{color:#d48c1c}
.teal{color:#5dcaa5}
@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
.animate-up{animation:fadeUp .25s ease both}

/* ── MESSAGES LAYOUT ── */
.messages-layout{display:grid;grid-template-columns:320px 1fr;flex:1;overflow:hidden;height:calc(100vh - 57px)}

/* ── THREAD LIST ── */
.thread-list{border-right:0.5px solid rgba(245,240,232,0.06);display:flex;flex-direction:column;overflow:hidden;background:#0e0c11}
.tl-artist-head{padding:14px 14px 0}
.broadcast-btn{width:100%;background:rgba(212,140,28,0.08);border:0.5px solid rgba(212,140,28,0.2);border-radius:9px;padding:9px 14px;font-size:12px;font-weight:500;color:#d48c1c;font-family:inherit;cursor:pointer;display:flex;align-items:center;gap:8px;transition:background .18s}
.broadcast-btn:hover{background:rgba(212,140,28,0.15)}
.tl-tabs{display:flex;border-bottom:0.5px solid rgba(245,240,232,0.06);padding:0 4px;flex-shrink:0}
.tlt{background:none;border:none;border-bottom:2px solid transparent;padding:10px 12px;font-size:12px;color:rgba(245,240,232,0.4);font-family:inherit;cursor:pointer;transition:all .18s;margin-bottom:-0.5px;display:flex;align-items:center;gap:5px;white-space:nowrap}
.tlt:hover{color:rgba(245,240,232,0.7)}
.tlt.active{color:#f5f0e8;border-bottom-color:#d48c1c}
.tlt-count{background:rgba(212,140,28,0.15);color:#d48c1c;font-size:10px;font-weight:700;border-radius:10px;padding:1px 5px}

/* Broadcast form */
.broadcast-form{margin:10px 12px;background:rgba(212,140,28,0.06);border:0.5px solid rgba(212,140,28,0.2);border-radius:10px;padding:12px;display:flex;flex-direction:column;gap:9px}
.bf-label{font-size:11px;color:#d48c1c;font-weight:500}
.bf-input{background:rgba(245,240,232,0.05);border:0.5px solid rgba(245,240,232,0.1);border-radius:8px;padding:9px 12px;font-size:13px;color:#f5f0e8;font-family:inherit;outline:none;resize:none;min-height:70px;line-height:1.5}
.bf-input::placeholder{color:rgba(245,240,232,0.22)}
.bf-foot{display:flex;justify-content:flex-end;gap:7px}
.bf-cancel{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:7px;padding:6px 12px;font-size:11px;color:rgba(245,240,232,0.35);font-family:inherit;cursor:pointer}
.bf-send{background:#d48c1c;border:none;border-radius:7px;padding:6px 14px;font-size:11px;font-weight:500;color:#0c0a0e;font-family:inherit;cursor:pointer}

/* Thread items */
.thread-items{flex:1;overflow-y:auto}
.thread-item{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;cursor:pointer;transition:background .18s;position:relative;border-bottom:0.5px solid rgba(245,240,232,0.04)}
.thread-item:hover{background:rgba(245,240,232,0.03)}
.thread-item.active{background:rgba(212,140,28,0.06);border-right:2px solid #d48c1c}
.ti-av-wrap{position:relative;flex-shrink:0}
.ti-av{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;color:rgba(255,255,255,0.9)}
.ti-online{position:absolute;bottom:1px;right:1px;width:9px;height:9px;border-radius:50%;background:#5dcaa5;border:2px solid #0e0c11}
.ti-body{flex:1;min-width:0}
.ti-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:2px}
.ti-name{font-size:13px;font-weight:500;color:#f5f0e8}
.ti-time{font-size:10px;color:rgba(245,240,232,0.3);white-space:nowrap}
.ti-context{font-size:10px;color:rgba(245,240,232,0.35);margin-bottom:3px}
.ti-preview{font-size:12px;color:rgba(245,240,232,0.4);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ti-preview-unread{color:rgba(245,240,232,0.75);font-weight:500}
.ti-unread-dot{width:7px;height:7px;border-radius:50%;background:#d48c1c;flex-shrink:0;margin-top:16px}

/* Locked artists */
.locked-artists-section{padding:8px 0}
.las-label{font-size:10px;color:rgba(245,240,232,0.3);letter-spacing:0.5px;padding:8px 14px 6px}
.locked-artist-row{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;transition:background .18s;border-bottom:0.5px solid rgba(245,240,232,0.03)}
.locked-artist-row:hover{background:rgba(245,240,232,0.03)}
.lar-av{width:36px;height:36px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;color:rgba(255,255,255,0.9)}
.lar-info{flex:1}
.lar-name{font-size:13px;color:rgba(245,240,232,0.6)}
.lar-genre{font-size:11px;color:rgba(245,240,232,0.3);margin-top:1px}
.lar-price{display:flex;align-items:center;gap:5px;font-size:12px;color:rgba(245,240,232,0.35)}
.lar-full{color:rgba(245,240,232,0.45)}
.lar-discount{color:#5dcaa5;font-weight:500}

/* ── THREAD VIEW ── */
.thread-view{display:flex;flex-direction:column;position:relative;overflow:hidden}

/* Paywall overlay */
.paywall-overlay{position:absolute;inset:0;background:rgba(12,10,14,0.85);display:flex;align-items:center;justify-content:center;z-index:100;backdrop-filter:blur(4px)}
.paywall-card{background:#13111a;border:0.5px solid rgba(245,240,232,0.1);border-radius:16px;padding:32px;max-width:380px;width:90%;display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center}
.pw-av{width:64px;height:64px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:500;color:rgba(255,255,255,0.9)}
.pw-name{font-size:18px;font-weight:500;color:#f5f0e8;letter-spacing:-0.2px}
.pw-genre{font-size:12px;color:rgba(245,240,232,0.4)}
.pw-divider{width:100%;height:0.5px;background:rgba(245,240,232,0.08);margin:4px 0}
.pw-title{font-size:15px;font-weight:500;color:#f5f0e8}
.pw-desc{font-size:13px;color:rgba(245,240,232,0.5);line-height:1.6;max-width:300px}
.pw-breakdown{width:100%;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:10px;padding:13px;display:flex;flex-direction:column;gap:8px;text-align:left}
.pwb-row{display:flex;justify-content:space-between;font-size:13px;color:rgba(245,240,232,0.55)}
.pwb-discount{display:flex;align-items:center;gap:7px}
.pwb-strike{text-decoration:line-through;color:rgba(245,240,232,0.3);font-size:12px}
.pwb-new{color:#5dcaa5;font-weight:500}
.pwb-sub-note{font-size:11px;color:rgba(245,240,232,0.35)}
.pwb-total{font-size:14px;font-weight:500;color:#f5f0e8;border-top:0.5px solid rgba(245,240,232,0.08);padding-top:8px;margin-top:2px}
.pw-split-note{font-size:11px;color:rgba(245,240,232,0.3)}
.pw-pay-btn{width:100%;background:#d48c1c;border:none;border-radius:10px;padding:12px;font-size:14px;font-weight:500;color:#0c0a0e;font-family:inherit;cursor:pointer;transition:opacity .18s;margin-top:4px}
.pw-pay-btn:hover{opacity:.88}
.pw-cancel{background:none;border:none;font-size:12px;color:rgba(245,240,232,0.3);font-family:inherit;cursor:pointer}

/* Broadcast success */
.broadcast-success{position:absolute;top:16px;left:50%;transform:translateX(-50%);background:rgba(93,202,165,0.1);border:0.5px solid rgba(93,202,165,0.3);border-radius:20px;padding:8px 18px;font-size:13px;color:#5dcaa5;display:flex;align-items:center;gap:8px;z-index:10;white-space:nowrap}

/* Empty state */
.thread-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center;color:rgba(245,240,232,0.3)}
.te-title{font-size:16px;font-weight:500;color:rgba(245,240,232,0.4)}
.te-sub{font-size:13px;color:rgba(245,240,232,0.3);max-width:260px;line-height:1.5}

/* Open thread */
.thread-open{display:flex;flex-direction:column;height:100%}
.to-head{display:flex;align-items:center;gap:12px;padding:14px 20px;border-bottom:0.5px solid rgba(245,240,232,0.06);flex-shrink:0}
.toh-av{width:40px;height:40px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;color:rgba(255,255,255,0.9)}
.toh-info{flex:1}
.toh-name{font-size:14px;font-weight:500;color:#f5f0e8}
.toh-context{font-size:11px;color:rgba(245,240,232,0.4);margin-top:2px}
.toh-unlocked{display:flex;align-items:center;gap:5px;font-size:11px;color:#5dcaa5;background:rgba(93,202,165,0.07);border:0.5px solid rgba(93,202,165,0.18);border-radius:20px;padding:4px 10px;flex-shrink:0}
.toh-paid-badge{display:flex;align-items:center;gap:5px;font-size:11px;color:#d48c1c;background:rgba(212,140,28,0.07);border:0.5px solid rgba(212,140,28,0.2);border-radius:20px;padding:4px 10px;flex-shrink:0}

/* Messages */
.to-messages{flex:1;overflow-y:auto;padding:20px;display:flex;flex-direction:column;gap:12px}
.msg-bubble-wrap{display:flex;align-items:flex-end;gap:8px}
.msg-bubble-wrap.is-mine{flex-direction:row-reverse}
.mb-av{width:28px;height:28px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:500;color:rgba(255,255,255,0.9)}
.msg-bubble{max-width:65%;display:flex;flex-direction:column;gap:3px}
.bubble-theirs{background:rgba(245,240,232,0.06);border:0.5px solid rgba(245,240,232,0.08);border-radius:0 12px 12px 12px;padding:10px 13px}
.bubble-mine{background:rgba(212,140,28,0.12);border:0.5px solid rgba(212,140,28,0.2);border-radius:12px 0 12px 12px;padding:10px 13px}
.mb-text{font-size:13px;color:#f5f0e8;line-height:1.5}
.mb-time{font-size:10px;color:rgba(245,240,232,0.3);margin-top:2px}
.msg-bubble-wrap.is-mine .mb-time{text-align:right}

/* Input */
.to-input{padding:14px 16px;border-top:0.5px solid rgba(245,240,232,0.06);flex-shrink:0}
.input-locked{display:flex;align-items:center;gap:9px;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:10px;padding:11px 14px;font-size:13px;color:rgba(245,240,232,0.35)}
.input-locked span{flex:1}
.il-cta{background:none;border:none;font-size:13px;color:#d48c1c;font-family:inherit;cursor:pointer;font-weight:500}
.input-open{display:flex;gap:9px;align-items:center}
.msg-input{flex:1;background:rgba(245,240,232,0.05);border:0.5px solid rgba(245,240,232,0.1);border-radius:10px;padding:11px 14px;font-size:13px;color:#f5f0e8;font-family:inherit;outline:none;transition:border-color .18s}
.msg-input:focus{border-color:rgba(212,140,28,0.35)}
.msg-input::placeholder{color:rgba(245,240,232,0.25)}
.msg-send{width:40px;height:40px;border-radius:10px;background:rgba(212,140,28,0.15);border:0.5px solid rgba(212,140,28,0.3);display:flex;align-items:center;justify-content:center;color:#d48c1c;cursor:pointer;flex-shrink:0;transition:background .18s}
.msg-send:hover{background:rgba(212,140,28,0.25)}
</style>